import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, languages } from '../translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Initialize state from localStorage synchronously
  const getInitialLanguage = () => {
    if (typeof window === 'undefined') return 'en';
    const saved = localStorage.getItem('propindexaLanguage');
    if (saved && translations[saved]) return saved;
    
    // Auto-detect from browser
    const browserLang = navigator.language.split('-')[0];
    const supportedLang = languages.find(l => l.code === browserLang);
    return supportedLang ? supportedLang.code : 'en';
  };

  const getInitialRTL = () => {
    const langCode = getInitialLanguage();
    const lang = languages.find(l => l.code === langCode);
    return lang?.rtl || false;
  };

  const [currentLanguage, setCurrentLanguage] = useState(getInitialLanguage);
  const [isRTL, setIsRTL] = useState(getInitialRTL);

  // Update document direction for RTL languages
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
  }, [isRTL, currentLanguage]);

  const changeLanguage = (langCode) => {
    if (translations[langCode]) {
      setCurrentLanguage(langCode);
      const lang = languages.find(l => l.code === langCode);
      setIsRTL(lang?.rtl || false);
      localStorage.setItem('propindexaLanguage', langCode);
    }
  };

  // Translation function with nested key support
  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations['en'];
        for (const fk of keys) {
          if (value && typeof value === 'object' && fk in value) {
            value = value[fk];
          } else {
            return fallback || key;
          }
        }
        return value;
      }
    }
    
    return value || fallback || key;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    languages,
    isRTL
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
